import {
  Badge,
  Button,
  Input,
  Label,
  PageHeader,
  Textarea,
  TOUCH,
  useDebouncedValue,
  useUnsavedChanges,
} from "@nymbl/nos2-rules";
import { Eye, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  type TemplateContent,
  type TemplateDetail,
  usePreviewEmailTemplate,
  useUpdateEmailTemplate,
} from "@/hooks/use-email";

interface EditTemplatePageProps {
  onClose: () => void;
  template: TemplateDetail;
}

function paragraphsFromText(value: string): string[] {
  return value
    .split("\n\n")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

function paragraphsToText(paragraphs: string[]): string {
  return paragraphs.join("\n\n");
}

/** One template's copy and live preview: a page, as the two side by side need the room. */
export function EditTemplatePage({ onClose, template }: EditTemplatePageProps) {
  const [subject, setSubject] = useState(template.content.subject);
  const [preheader, setPreheader] = useState(template.content.preheader);
  const [heading, setHeading] = useState(template.content.heading);
  const [bodyText, setBodyText] = useState(paragraphsToText(template.content.bodyParagraphs));
  const [ctaLabel, setCtaLabel] = useState(template.content.ctaLabel ?? "");
  const [previewMode, setPreviewMode] = useState<"html" | "text">("html");

  const save = useUpdateEmailTemplate(template.key);
  const preview = usePreviewEmailTemplate(template.key);

  const content: TemplateContent = useMemo(
    () => ({
      subject,
      preheader,
      heading,
      bodyParagraphs: paragraphsFromText(bodyText),
      ctaLabel: ctaLabel.trim() === "" ? null : ctaLabel,
    }),
    [subject, preheader, heading, bodyText, ctaLabel],
  );

  const debouncedContent = useDebouncedValue(content, 400);
  const triggerPreview = preview.mutate;

  useEffect(() => {
    triggerPreview(debouncedContent);
  }, [debouncedContent, triggerPreview]);

  const dirty =
    subject !== template.content.subject ||
    preheader !== template.content.preheader ||
    heading !== template.content.heading ||
    bodyText !== paragraphsToText(template.content.bodyParagraphs) ||
    ctaLabel !== (template.content.ctaLabel ?? "");
  const { leave } = useUnsavedChanges(dirty);

  const sampleEntries = Object.entries(template.sampleData).filter(
    ([, v]) => typeof v === "string" || typeof v === "number" || typeof v === "boolean",
  );

  function handleSave() {
    save.mutate(content, {
      onSuccess: () => leave(onClose),
    });
  }

  function handleReset() {
    setSubject(template.defaultContent.subject);
    setPreheader(template.defaultContent.preheader);
    setHeading(template.defaultContent.heading);
    setBodyText(paragraphsToText(template.defaultContent.bodyParagraphs));
    setCtaLabel(template.defaultContent.ctaLabel ?? "");
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit template"
        badges={
          <Badge variant="outline" className="font-mono text-xs">
            {template.key}
          </Badge>
        }
        actions={
          <>
            <Button variant="outline" className={TOUCH} onClick={handleReset}>
              Reset to default
            </Button>
            <Button variant="outline" className={TOUCH} onClick={() => leave(onClose)}>
              Cancel
            </Button>
            <Button className={TOUCH} onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Form */}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="subject">Subject</Label>
            <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="preheader">Preheader</Label>
            <Input
              id="preheader"
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Snippet shown next to the subject in most inboxes.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="heading">Heading</Label>
            <Input id="heading" value={heading} onChange={(e) => setHeading(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="body">Body</Label>
            <Textarea
              id="body"
              rows={8}
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="One paragraph per block. Separate paragraphs with a blank line."
            />
            <p className="text-xs text-muted-foreground">
              Blank line between paragraphs. Markdown is not supported.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cta">CTA label</Label>
            <Input
              id="cta"
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              placeholder="Leave empty to hide the button"
            />
            <p className="text-xs text-muted-foreground">
              The button URL is provided by the caller (e.g. <code>appUrl</code>,{" "}
              <code>resetUrl</code>) and isn't editable.
            </p>
          </div>

          {sampleEntries.length > 0 && (
            <div className="rounded-md border border-border/50 bg-muted/30 p-3 text-xs">
              <div className="mb-1.5 font-medium text-foreground">
                Available placeholders (preview values)
              </div>
              <div className="grid gap-1 font-mono">
                {sampleEntries.map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-primary">{`{{${k}}}`}</span>
                    <span className="text-muted-foreground">→ {String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="flex min-h-[500px] flex-col rounded-md border border-border/50 bg-muted/20">
          <div className="flex items-center justify-between border-b border-border/50 px-3 py-2">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Eye className="h-3.5 w-3.5" />
              Live preview
              {preview.isPending && <LoaderCircle className="size-3 animate-spin" />}
            </div>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={previewMode === "html" ? "secondary" : "ghost"}
                onClick={() => setPreviewMode("html")}
                className={`h-7 px-2 text-xs ${TOUCH}`}
              >
                HTML
              </Button>
              <Button
                size="sm"
                variant={previewMode === "text" ? "secondary" : "ghost"}
                onClick={() => setPreviewMode("text")}
                className={`h-7 px-2 text-xs ${TOUCH}`}
              >
                Plaintext
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden bg-background">
            {preview.isError ? (
              <div className="flex h-full items-center justify-center p-4 text-sm text-destructive">
                {preview.error?.message ?? "Failed to render preview"}
              </div>
            ) : previewMode === "html" ? (
              <iframe
                title="Email preview"
                srcDoc={preview.data?.html ?? ""}
                className="h-full w-full"
                sandbox=""
              />
            ) : (
              <pre className="h-full overflow-auto p-4 text-xs leading-relaxed text-foreground">
                {preview.data?.text ?? ""}
              </pre>
            )}
          </div>
          <div className="border-t border-border/50 px-3 py-2 text-xs text-muted-foreground">
            Subject:{" "}
            <span className="font-medium text-foreground">{preview.data?.subject ?? subject}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
